import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WashingMachine, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

const COMMON_LAUNDRY_ITEMS = [
  'Jeans',
  'T-shirts',
  'Dress shirts',
  'Blouses',
  'Sweaters',
  'Hoodies',
  'Underwear',
  'Socks',
  'Pajamas',
  'Workout clothes',
  'Swimwear',
  'Jackets',
  'Dresses',
  'Shorts',
  'Towels',
  'Bedsheets',
];

interface LaundryItem {
  id: string;
  name: string;
  completed: boolean;
}

export function PreTripLaundry() {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<LaundryItem[]>([]);
  const [newItem, setNewItem] = useState('');
  const [selectKey, setSelectKey] = useState(0);

  const isDuplicate = (name: string) => {
    return items.some(item => item.name.toLowerCase() === name.toLowerCase());
  };

  const addItem = () => {
    if (!newItem.trim()) return;
    
    setItems(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: newItem.trim(),
        completed: false
      }
    ]);
    setNewItem('');
  };

  const toggleItem = (id: string) => {
    setItems(prev => 
      prev.map(item => 
        item.id === id ? { ...item, completed: !item.completed } : item
      )
    );
  };

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addItem();
    }
  };

  const addFromDropdown = (value: string) => {
    if (isDuplicate(value)) return;
    setItems(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: value,
        completed: false
      }
    ]);
    setSelectKey(prev => prev + 1);
  };

  const completedCount = items.filter(i => i.completed).length;

  return (
    <Card className="border-cyan-500/20 shadow-md shadow-cyan-500/5 overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 cursor-pointer transition-colors bg-gradient-to-br from-cyan-50/60 to-transparent dark:from-cyan-950/15 dark:to-transparent hover:from-cyan-50 dark:hover:from-cyan-950/25">
            <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-sm shadow-cyan-500/20">
                <WashingMachine className="h-4 w-4" />
              </span>
              Pre-Trip Laundry
              {items.length > 0 && (
                <span className="ml-auto text-xs font-semibold text-cyan-700 dark:text-cyan-300 bg-cyan-500/10 px-2 py-0.5 rounded-full">
                  {completedCount}/{items.length}
                </span>
              )}
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm pl-11">
              Items to wash or prepare before your trip
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-3">
            {/* Quick add from dropdown */}
            <Select
              key={selectKey}
              onValueChange={addFromDropdown}
            >
              <SelectTrigger className="w-full bg-background">
                <SelectValue placeholder="Quick add common items..." />
              </SelectTrigger>
              <SelectContent className="bg-popover z-50">
                {COMMON_LAUNDRY_ITEMS.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Add custom item */}
            <div className="flex gap-2">
              <Input
                placeholder="Or type custom item..."
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1"
              />
              <Button 
                size="icon" 
                onClick={addItem}
                disabled={!newItem.trim()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Laundry list */}
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No items yet. Add clothes to wash before packing!
              </p>
            ) : (
              <div className="space-y-2">
                {items.map(item => (
                  <div 
                    key={item.id} 
                    className="flex items-center gap-3 p-2 rounded-lg border border-border hover:bg-muted/30 transition-colors group"
                  >
                    <Checkbox
                      checked={item.completed}
                      onCheckedChange={() => toggleItem(item.id)}
                    />
                    <span className={`flex-1 text-sm ${item.completed ? 'line-through text-muted-foreground' : ''}`}>
                      {item.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removeItem(item.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Progress indicator */}
            {items.length > 0 && completedCount === items.length && (
              <p className="text-sm text-green-600 dark:text-green-400 text-center font-medium">
                ✓ All items ready for packing!
              </p>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
